import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core'
import { ModalContainerComponent } from '@components-library/tb-modal-manager/modal-container-component/modal-container.component'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { TbToggleComponent } from '@components-library/tb-toggle/tb-toggle.component'
import { MatIconModule } from '@angular/material/icon'
import { TranslocoModule } from '@jsverse/transloco'
import {
    ViewsTypeUI,
    ViewUI,
} from '@app/feature/header/workspace-controls/advanced-options/view-section/view-section.component'
import { TbTab } from '@components-library/tb-tab/tb-tab.component'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { ModalLayoutButtonsComponent } from '@components-library/tb-modal-manager/modal-layout-buttons/modal-layout-buttons.component'
import { TbIconComponent } from '@components-library/tb-icon/tb-icon.component'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { reduce } from 'lodash-es'
import { WorkspaceViewRecord } from '@models/ui'
import { dirtyCheck } from '@ngneat/dirty-check-forms'
import { of } from 'rxjs'

export interface ViewConfigurationData {
    schemas: TbTab[]
    views: ViewsTypeUI
}

type SchemaViewActiveControlGroup = {
    [schemaGuid: string]: FormGroup<ViewsActiveControlGroup>
}

type ViewsActiveControlGroup = { [viewName: string]: FormControl<boolean> }

@UntilDestroy()
@Component({
    selector: 'app-select-views-settings',
    templateUrl: './select-views-settings.component.html',
    imports: [
        TranslocoModule,
        MatIconModule,
        TbToggleComponent,
        TbButtonComponent,
        ModalLayoutComponent,
        ModalLayoutButtonsComponent,
        TbIconComponent,
        ReactiveFormsModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectViewsSettingsComponent
    extends ModalContainerComponent<ViewConfigurationData, WorkspaceViewRecord>
    implements OnInit
{
    activeViewControl!: FormGroup<SchemaViewActiveControlGroup>

    isDirty = false

    ngOnInit() {
        this.activeViewControl = new FormGroup(this.setControlGroup())
        this.setCheckOnDirty()
    }

    applyChanges() {
        this.close(this.activeViewControl.getRawValue() as WorkspaceViewRecord)
    }

    hasChanged(): boolean {
        return this.isDirty
    }

    private setControlGroup(): SchemaViewActiveControlGroup {
        return reduce(
            this.data.views,
            (control, views, key) => {
                control[key] = new FormGroup(this.combineControlGroupsFromViews(views))
                return control
            },
            {} as SchemaViewActiveControlGroup,
        )
    }

    private combineControlGroupsFromViews(views: ViewUI[]): ViewsActiveControlGroup {
        return views.reduce((control, view) => {
            control[view.name] = new FormControl(view.active, { nonNullable: true })
            return control
        }, {} as ViewsActiveControlGroup)
    }

    private setCheckOnDirty() {
        dirtyCheck(this.activeViewControl, of(this.activeViewControl.value), {
            debounce: 0,
            useBeforeunloadEvent: false,
        })
            .pipe(untilDestroyed(this))
            .subscribe((isDirty) => {
                this.isDirty = isDirty
            })
    }
}
